import { apiFetch } from "./api.js";
import { compressImage, type CompressionConfig } from "./compressImage.js";
import { validateUploadSize, type UploadResult, type PresignResponse } from "@homethyst/shared/photo";

export type { UploadResult };

async function uploadBlob(blob: Blob, uploadUrl: string, maxBytes?: number): Promise<void> {
  validateUploadSize(blob.size, maxBytes);
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: {
      "Content-Type": "image/jpeg",
      "x-amz-server-side-encryption": "AES256",
    },
  });
  if (!res.ok) throw new Error("Photo upload failed");
}

/**
 * Compress and upload a photo with tier-appropriate renditions.
 * Returns S3 keys for each rendition.
 */
export async function uploadPhoto(file: File, config?: CompressionConfig): Promise<UploadResult> {
  const renditions = await compressImage(file, config);

  const getUrl = () => apiFetch<PresignResponse>("/api/photos/upload-url", {
    method: "POST",
    body: JSON.stringify({ mimeType: "image/jpeg" }),
  });

  const displayUpload = getUrl();
  const thumbUpload = getUrl();
  const originalUpload = renditions.originalBlob ? getUrl() : undefined;

  const [display, thumb, original] = await Promise.all([displayUpload, thumbUpload, originalUpload]);

  const uploads: Promise<void>[] = [
    uploadBlob(renditions.displayBlob, display.uploadUrl, display.maxBytes),
    uploadBlob(renditions.thumbBlob, thumb.uploadUrl, thumb.maxBytes),
  ];
  if (original && renditions.originalBlob) {
    uploads.push(uploadBlob(renditions.originalBlob, original.uploadUrl, original.maxBytes));
  }
  await Promise.all(uploads);

  return {
    displayKey: display.key,
    thumbKey: thumb.key,
    originalKey: original?.key,
  };
}
